import React, { useRef, useCallback, memo } from 'react';
import { useDispatch } from 'react-redux';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { userSelector } from 'selectors';
import useSize from '@react-hook/size';

import { setSideMenuOpen } from 'actions/app_action';

import { Divider, Flex, Text } from 'common/common-components.styled';
import { PrefixForId } from 'common/enums';
import type { User } from 'common/interfaces';

import Backdrop from 'components/backdrop/index';
import { LogoMobexo } from 'components/icons/logo-mobexo';
import { MobexoLogoIcon } from 'components/icons/mobexo-logo-icon';
import MainRoleSwitcher from 'components/main-role-switcher';

import { NotificationsDrawerPopoverContainer, NotificationsList, NotificationsProvider } from 'widgets/notifications';

import { useTypedSelector } from 'hooks/use-typed-selector';
import useWindowSize from 'hooks/use-window-size';

import { SIDE_MENU_ACTION_BAR_VERTICAL_HEIGHT, SIDE_MENU_ACTION_BAR_HORIZONTAL_HEIGHT } from './constants';
import { useIsMinimized } from './hooks/use-is-minimized';
import Menu from './menu';
import SideMenuActionBar from './side-menu-action-bar';
import UserInfoContainer from './user-info-container';
import { ScrollbarContainer, StyledHeader, StyledSideMenu } from './side-menu.styled';

interface MenuHeaderProps {
  isMinimized: boolean;
}

interface RoleDisplayProps {
  user: User;
}

const MenuHeader = ({ isMinimized }: MenuHeaderProps) => (
  <StyledHeader isMinimized={isMinimized}>{isMinimized ? <LogoMobexo /> : <MobexoLogoIcon />}</StyledHeader>
);

const RoleDisplay = memo(function RoleDisplay({ user }: RoleDisplayProps) {
  if (!user.roles.length) return null;

  if (user.roles.length === 1) {
    return (
      <Text padding='10px 0 10px 15px' size='14' color='grey600'>
        {user.mainRole.name}
      </Text>
    );
  }

  return <MainRoleSwitcher textColor='grey600' />;
});

const SideMenu = () => {
  const dispatch = useDispatch();
  const sideMenuOpen: boolean = useTypedSelector(state => state['app'].sideMenuOpen);
  const user: User = useTypedSelector(userSelector);

  const { isMinimized, setIsMinimized } = useIsMinimized();
  const perfectScrollbarContainerRef = useRef<HTMLDivElement | null>(null);
  useSize(perfectScrollbarContainerRef.current);

  const { windowWidth } = useWindowSize();
  const isDesktopWidth = windowWidth >= 1200;

  const handleCloseSideMenu = useCallback(() => {
    dispatch(setSideMenuOpen(false));
  }, [dispatch]);

  const handleToggleMinimize = useCallback(() => {
    setIsMinimized(!isMinimized);
  }, [isMinimized, setIsMinimized]);

  return (
    <>
      <Backdrop zIndex={100} show={sideMenuOpen} click={handleCloseSideMenu} />

      <StyledSideMenu
        isMinimized={isMinimized}
        open={sideMenuOpen}
        isDesktop={isDesktopWidth}
        role='navigation'
        aria-label='Main Navigation'
      >
        <MenuHeader isMinimized={isMinimized} />

        <Flex minHeight='40px' align='center' margin='30px 17px 0'>
          <UserInfoContainer isMinimized={isMinimized} />
        </Flex>

        {!isDesktopWidth && (
          <Flex padding='5px 0 0 45px'>
            <RoleDisplay user={user} />
          </Flex>
        )}

        <Divider color='grey50' margin='15px 15px 0' />

        <PerfectScrollbar id={PrefixForId.SideMenu}>
          <ScrollbarContainer
            ref={perfectScrollbarContainerRef}
            actionBarHeight={
              isMinimized ? SIDE_MENU_ACTION_BAR_VERTICAL_HEIGHT : SIDE_MENU_ACTION_BAR_HORIZONTAL_HEIGHT
            }
            isDesktopWidth={isDesktopWidth}
          >
            <Menu isMinimized={isMinimized} />
          </ScrollbarContainer>
        </PerfectScrollbar>
      </StyledSideMenu>

      {isDesktopWidth && (
        <NotificationsProvider>
          <SideMenuActionBar isMinimized={isMinimized} onToggleMinimized={handleToggleMinimize} />
          <NotificationsDrawerPopoverContainer direction={isMinimized ? 'horizontal' : 'vertical'}>
            <NotificationsList />
          </NotificationsDrawerPopoverContainer>
        </NotificationsProvider>
      )}
    </>
  );
};

export default SideMenu;
